import { useCallback, useMemo } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useNavigate } from 'react-router-dom'
import { useCurrentUser, usePermissions } from '@/features/auth/state/auth'

import { formatCNPJ } from '@/utils/format'

import { ROUTE_BUILDERS } from '@/config/routes'
import { useTenantContext } from '@/context/tenant'
import type { ClientItem } from '@/types/client'
import { useToast } from '@/lib/hooks/use-toast'
import ClientStatusTag from './client-status-tag'
import { useFetchClientByOwner } from '../services/useFetchClientByOwner'

export default function ListarClienteOwner({
  id_owner,
  id_client
}: {
  id_owner?: string | null
  id_client?: string | null
}) {
  const user = useCurrentUser()
  const permissions = usePermissions()
  const { toast } = useToast()
  const navigate = useNavigate()
  const { selectedTenant } = useTenantContext()
  const { data: allClientsRaw = [] } = useFetchClientByOwner(id_owner || '')

  // ---- helpers p/ key estável + dedup ----
  const onlyDigits = (v?: string | null) => (v ?? '').replace(/\D/g, '')

  // Gera chave estável priorizando id_client e CNPJ/CPF
  const getClientKey = (c: Partial<ClientItem> & Record<string, any>) => {
    const id = String(c.id_client ?? '').trim()
    if (id) return id
    const cnpjcpf = onlyDigits(String(c.cnpjcpf ?? c.cnpj ?? c.cpf ?? ''))
    if (cnpjcpf) return cnpjcpf
    // fallback combinando campos estáveis para casos raros sem id e sem doc
    return `${(c.razao_social ?? '').trim()}|${(c.id_tenant ?? '').trim()}`
  }

  const isInvalidClient = (c: any, id_client: string) => {
    const id = onlyDigits(String(id_client ?? ''))
    const doc = onlyDigits(String(c?.cnpjcpf ?? ''))
    return id && doc && id === doc
  }

  // const allClients = useMemo(() => (Array.isArray(allClientsRaw) ? allClientsRaw : []), [allClientsRaw])

  const clients = useMemo(() => {
    const map = new Map<string, any>()
    for (const c of Array.isArray(allClientsRaw) ? allClientsRaw : []) {
      if (isInvalidClient(c, id_client ?? '')) continue
      const k = getClientKey(c)
      if (!map.has(k)) map.set(k, c)
    }
    return Array.from(map.values())
  }, [allClientsRaw])

  const canNavigateToClient = useCallback(
    (client: ClientItem) => {
      if (user?.typeUser === 'ADMIN') {
        if (permissions?.permission_product || permissions?.permission_geral) return true
        if (user?.typeCargo === 'CEO') return true
      } else if (user?.typeUser === 'USUARIO') {
        if (user?.typeCargo === 'CEO' && user?.id_tenant === client.id_tenant) return true
        if (
          (user?.id === client.id_responsavel || user?.id === client.id_gerente) &&
          user?.id_tenant === client.id_tenant
        ) {
          return true
        }
      }
      return false
    },
    [user, permissions]
  )

  const handleNavigateCliente = (client: ClientItem) => {
    if (!canNavigateToClient(client)) {
      toast({ variant: 'destructive', description: 'Você não tem permissão para acessar este cliente.' })
      return
    }
    if (selectedTenant && user?.typeUser === 'ADMIN') {
      navigate(ROUTE_BUILDERS.clientDashboardAdminInfo(selectedTenant.id, client.id_client || client.cnpjcpf), {
        state: { client }
      })
    } else if (user?.typeUser === 'USUARIO') {
      navigate(ROUTE_BUILDERS.clientDashboardInfo(client.id_client || client.cnpjcpf), { state: { client } })
    }
  }

  return (
    <>
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[200px] md:w-[300px]">Razão Social</TableHead>
            <TableHead className="hidden md:block ">CNPJ</TableHead>
            <TableHead className="table-cell">Status</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {(clients ?? []).map((client: any) => {
            const rowKey = getClientKey(client)
            const cnpjcpf = onlyDigits(String(client.cnpjcpf ?? client.id_client ?? ''))
            const cnpjDisplay = cnpjcpf ? formatCNPJ(cnpjcpf) : 'N/A'

            return (
              <TableRow
                key={rowKey}
                className="cursor-pointer hover:bg-muted transition"
                onClick={() => handleNavigateCliente(client)}
              >
                <TableCell className="font-medium">{client.razao_social || 'N/A'}</TableCell>
                <TableCell className="hidden text-center md:block">{cnpjDisplay}</TableCell>
                <TableCell className="font-medium">
                  <ClientStatusTag status={client.status} />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {(clients?.length ?? 0) === 0 && (
        <div className="mt-4 flex flex-col items-center">
          <p className="mt-4 text-lg text-foreground">Nenhum CNPJ relacionado encontrado.</p>
        </div>
      )}
    </>
  )
}
